import _ from 'lodash';
import {
  NEW_WORK_COLOR,
  REFACTOR_COLOR,
  REWORK_COLOR
} from 'common/constants/_colors';
import moment from 'moment';
import {
  numeralify,
  roundTo100Percents,
  calculatePercentage
} from 'common/utils/utils';
import { SEARCH_DATE_FORMAT } from 'common/constants/constants';
import { GRANULARITY } from '../performance/views/CustomReport/consts';

export const WORKING_DAYS_WEEK_TYPE = {
  '1-5': 'week',
  '1-6': 'week',
  '2-6': 'isoWeek'
};

export const getTimeRangesByGranularity = (
  start,
  end,
  granularity = GRANULARITY.DAYS,
  datesNotInclusive,
  // for data for 2000-01-01 for example
  // inclusive means:{ before: 2000-01-01, after: 2000-01-01 }
  // not inclusive means: { after: 2000-01-01, before: 2000-02-01 }
  workingDaysSetting = {} // this is needed in case of weekly granularity, to know whether to start from monday/sunday depending or setting
) => {
  const dateRanges = [];
  let currDate = moment(start).startOf('day');
  const lastDate = moment.min(
    moment().startOf('day'),
    moment(end).startOf('day')
  );
  while (currDate.startOf('day').diff(lastDate, 'day') <= 0) {
    const fromDate = currDate.clone();
    const toDate = currDate
      .clone()
      .endOf(
        granularity === GRANULARITY.WEEKS
          ? WORKING_DAYS_WEEK_TYPE[_.get(workingDaysSetting, 'value', '')] ||
              'isoWeek'
          : granularity
      );
    if (datesNotInclusive) {
      currDate = toDate;
      dateRanges.push({
        after: fromDate.format(SEARCH_DATE_FORMAT),
        before: toDate.add(1, 'day').format(SEARCH_DATE_FORMAT)
      });
    } else {
      dateRanges.push({
        after: fromDate.format(SEARCH_DATE_FORMAT),
        before: toDate.format(SEARCH_DATE_FORMAT)
      });
      currDate = toDate.add(1, 'day');
    }
  }
  // This was added in order to adjust request according to end date based on user selection (and not a whole week/month)
  if (dateRanges.length) {
    dateRanges[dateRanges.length - 1].before = end;
  }
  return dateRanges;
};

export const filterItrationsAccordingToDateRange = (
  start = moment(),
  end = moment(),
  iterations = []
) => {
  /**
   * Input: 2 date strings and the iterations list: ('2020-08-01', '2020-08-31', [
   *  {id: 123, planned_end_date: '2020-08-30', end_date: '2020-08-30', start_date: '2020-07-30'},
   *  {id: 124, planned_end_date: '2020-09-30', end_date: '2020-09-30', start_date: '2020-08-31'},
   *  {id: 125, planned_end_date: '2020-07-30', end_date: '2020-07-30', start_date: '2020-07-01'},
   * )
   * Output: filtered out iterations that are in the time range given:
   *  [
   *    {id: 123, planned_end_date: '2020-08-30', end_date: '2020-08-30', start_date: '2020-07-30'},
   *    {id: 124, planned_end_date: '2020-09-30', end_date: '2020-09-30', start_date: '2020-08-31'},
   *  ]
   */
  const mStart = moment(start);
  const mEnd = moment(end);

  const filteredIterations = iterations.filter(
    iteration =>
      iteration.planned_end_date &&
      (moment(iteration.start_date).isBetween(mStart, mEnd, undefined, '[]') ||
        moment(iteration.end_date).isBetween(mStart, mEnd, undefined, '[]'))
  );

  const uniqueIterations = _.uniqBy(
    filteredIterations,
    iteration => iteration.start_date + '_' + iteration.end_date
  );

  const orderedIterations = _.orderBy(uniqueIterations, ['end_date']);

  return orderedIterations.map(iteration => {
    return {
      after: iteration.start_date,
      before: iteration.end_date,
      name: iteration.name
    };
  });
};

export const calcWorkBreakDownData = ({
  newWork = null,
  rework = null,
  refactor = null
} = {}) => {
  const totalChanges = newWork + rework + refactor;
  const calcPercentage = value => calculatePercentage(value, totalChanges);
  const breakdownData = [
    { key: 'newWork', value: calcPercentage(newWork) },
    { key: 'refactor', value: calcPercentage(refactor) },
    { key: 'rework', value: calcPercentage(rework) }
  ];

  const roundedPcts = roundTo100Percents(breakdownData, 'value').reduce(
    (acc, { value, key }) => ({
      ...acc,
      [key]: value
    }),
    {}
  );
  return {
    ...roundedPcts,
    total:
      newWork !== null || rework !== null || refactor !== null
        ? numeralify(totalChanges)
        : '-',
    totalChanges
  };
};
export const getWorkBreakdownConfigurations = WorkBreakdownData => [
  {
    color: NEW_WORK_COLOR,
    text: 'New work',
    percent: WorkBreakdownData.newWork
  },
  {
    color: REFACTOR_COLOR,
    text: 'Refactor',
    percent: WorkBreakdownData.refactor
  },
  {
    color: REWORK_COLOR,
    text: 'Rework',
    percent: WorkBreakdownData.rework
  }
];
